import { useState, useEffect, useLayoutEffect, useCallback, useMemo, createContext, useContext, Fragment, useRef } from "react";
import {
  Stack,
  Typography,
  Box,
  IconButton,
  Divider,
  CircularProgress,
  styled,
  Tooltip,
  FilledInput,
  InputBase,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  SwipeableDrawer,
  useTheme,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import BookmarkIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkedIcon from "@mui/icons-material/Bookmark";
import PlayIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, useHistory, useLocation } from "react-router-dom";
import logger from "logrock";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  toastError,
  toSearchKey,
  searchPath,
  transliterationHint,
  rootAnalysisSpelling,
} from "../../utils/utils";
import Swal from "sweetalert2";

import { FileCopyOutlined as CopyIcon } from "@mui/icons-material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Capacitor } from "@capacitor/core";
import { stripHTMLTags, getRootSuggestionsDetailed } from "../../utils/utils";
import { retrieveAllWordsWithRoot, getMatchingRoots, getEntriesForRootKeys } from "../../utils/dictionary-db";
import { stripHarakat } from "../../utils/arabicNormalize";
import { rootKey, rootIdentity, rootSpelling, rememberTypedSpelling, typedSpellingOf, forgetTypedSpellings } from "../../utils/searchHistory";
import { spellRootKeys } from "../../utils/rootDisplay";
import { goBackOrHome } from "../../utils/navigation";
import { v4 as uuidv4 } from "uuid";
import { useAppContext } from "../../utils/AppContext";
import { toastSuccess, addToastSwipeToDismiss, toastTip, isReservedCollectionName } from "../../utils/utils";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import FeedbackDialog from "../FeedbackDialog";
import PageHeader from "../PageHeader";
import RootReveal from "../RootReveal";
import Walkthrough from "./Walkthrough";
import { trackBookmarkAdd, trackFlashcardAdd, trackReportMissingRoot, trackDefinitionViewed, trackRootNotFound } from "../../utils/analytics";
import scrollToTop from "../../utils/scrollToTop";

import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import useSearchHotkeys from "../../utils/useSearchHotkeys";
import { closeSoftKeyboard, onSoftKeyboardHidden } from "../../utils/softKeyboard";

const AlignCenterBox = styled(Box)(({ theme }) => ({
  ...theme.mixins.alignInTheCenter,
}));

// history decodes everything in the path except reserved characters.
const decodeRouteParam = (param) => {
  try {
    return decodeURIComponent(param);
  } catch {
    return param;
  }
};

// Hash history keeps a push's `state` only in memory: Back/forward and reload rebuild
// the location from the URL, which lost the typed spelling of a hamzated word (سؤال,
// key سوال) that root analysis needs. Keep it on the browser's own history entry too,
// and in storage for a page Android reopens without that state. A visit with nothing
// typed (a History card) records a null `raw`, so that neither Back to it nor a reopened
// page picks up the spelling of another visit.
const RAW_QUERY = "hwRawQuery";
const rememberRawQuery = (key, raw) => {
  try {
    window.history.replaceState({ ...window.history.state, [RAW_QUERY]: { key, raw } }, "");
  } catch {
    /* history state unavailable */
  }
  if (raw) rememberTypedSpelling(key, raw);
  else forgetTypedSpellings([key]);
};
// Storage is only for an entry with no record of its own: a page reopened in a new WebView.
const rememberedRawQuery = (key) => {
  const saved = window.history.state?.[RAW_QUERY];
  return saved && saved.key === key ? saved.raw ?? undefined : typedSpellingOf(key);
};

// The dictionary's spelling of a search key for one entry (سال -> سأل), taken from
// the entry's root or else its own words, as the Bookmarks page spells its keys. The
// key itself when there is no hamza to restore.
export const entrySpelling = (key, entry) =>
  rootSpelling(key, [
    entry?.root,
    ...(entry?.definitions || entry?.forms || []).map((d) => d.text),
    entry?.nouns?.[0]?.text,
  ]);

// Several entries can share a key (راي "salmon", رأي "to see"; بار, بأر "well"): name
// the key by the spelling that has a hamza when what was typed has one (آبار is from
// بأر), then by the one sharing most letters with it.
const HAMZA = /[ءأإآؤئ]/;
export const pickRootSpelling = (spellings, key, typed = "") => {
  const plain = stripHarakat(typed || "");
  const typedLetters = new Set(plain);
  const hamzaMatters = /[\u0600-\u06FF]/.test(plain);
  const score = (s) =>
    (hamzaMatters && HAMZA.test(s) === HAMZA.test(plain) ? 100 : 0) +
    [...s].filter((ch) => typedLetters.has(ch)).length;
  let best = null;
  for (const s of spellings) {
    if (best === null || score(s) > score(best)) best = s;
  }
  return best || key;
};

// Android 13+ confirms every copy with its own clipboard preview; an app toast on
// top of it is a second, overlapping confirmation.
export const systemConfirmsCopy = () => {
  if (Capacitor.getPlatform() !== "android") return false;
  const match = /Android (\d+)/.exec(navigator.userAgent);
  return !!match && Number(match[1]) >= 13;
};

// Card actions: at least a 48 dp touch target each, icons kept at 18 px.
const cardButtonSx = { width: 48, height: 48, "& svg": { fontSize: 18 } };
// Search-bar actions: 48 dp touch targets that take no more room in the bar's
// layout than the bare icons did (the negative margins reach into its padding).
const barButtonSx = { width: 48, height: 48, my: -1.5, mx: -0.75 };

// Opens the page's one "Add to Collection" sheet with a card's add actions. A sheet
// per card cost three document touch listeners per card, even while closed.
const CollectionSheetContext = createContext(() => {});

const CollectionSheet = ({ open, actions, onClose }) => {
  const { getCollectionNames } = useAppContext();
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // Swipe down on the sheet (its handle included) closes it; nothing opens it by swipe.
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      // Don't leave a hidden modal in the page while it's closed (the page treats any
      // .MuiModal-root as an open dialog).
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 1,
          pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "rgba(0,0,0,0.13)" }} />
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
        <Box sx={{ fontWeight: 800, fontSize: "0.85rem", color: "#888", fontFamily: "Mulish, sans-serif", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Add to Collection
        </Box>
      </Box>
      <List disablePadding sx={{ pb: 1 }}>
        {getCollectionNames().map((collectionName) => (
          <ListItemButton
            key={collectionName}
            sx={{ borderRadius: 2, py: 1.4, px: 2 }}
            onClick={() => {
              actions?.addTo(collectionName);
              onClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <StyleOutlinedIcon sx={{ color: "#1535BD", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={collectionName}
              primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif", fontSize: "0.95rem" }}
            />
          </ListItemButton>
        ))}
        {getCollectionNames().length > 0 && (
          <Box sx={{ mx: 2, my: 0.5, height: "1px", bgcolor: "rgba(0,0,0,0.07)" }} />
        )}
        <ListItemButton
          sx={{ borderRadius: 2, py: 1.4, px: 2 }}
          onClick={() => {
            onClose();
            actions?.addToNew();
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AddCircleOutlineOutlinedIcon sx={{ color: "#1535BD", fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="New Collection"
            primaryTypographyProps={{ fontWeight: 600, fontFamily: "Mulish, sans-serif", fontSize: "0.95rem", color: "#1535BD" }}
          />
        </ListItemButton>
      </List>
    </SwipeableDrawer>
  );
};

const CardButtons = ({
  shortNotifText,
  textToCopy,
  word,
  form,
  definition,
  transliteration,
  wordID,
  root,
  displayShort,
  setDisplayShort,
}) => {
  // pull the flashcard functions from the context
  const { addFlashcard, removeFlashcard, getCollectionNames, addCollectionWithFlashcard, flashcards, followCollection } =
    useAppContext();
  const openCollectionSheet = useContext(CollectionSheetContext);

  // state used to manage if this card has been added to flashcards
  const [addedToFlashcards, setAddedToFlashcards] = useState(false);

  // states used to manage push animation of button
  const [flashcardButtonPressed, setFlashcardButtonPressed] = useState(false);
  const [copyButtonPressed, setCopyButtonPressed] = useState(false);

  const handleCopy = () => {
    setCopyButtonPressed(true);
    if (!systemConfirmsCopy()) toastSuccess(`Successfully Copied ${shortNotifText} to Clipboard`);
    setTimeout(() => {
      setCopyButtonPressed(false);
    }, 1000);
  };

  const buttonStyles = {
    transition: "opacity 0.5s ease",
    opacity: copyButtonPressed ? 0.1 : 1,
  };

  const flashcardDefinition = () =>
    transliteration
      ? `<em style="display:block;color:#555;font-size:0.85em;margin-bottom:6px;font-style:italic">${transliteration}</em>${definition}`
      : definition;

  // Undo is only offered when this tap actually inserted the card: if the word was
  // already in the collection, Undo would delete the card saved earlier.
  // titleText (not title): collection names are user data and must never render as HTML.
  const announceFlashcardAdd = (addedTo, inserted, isNewCollection) => {
    // A sync can rename the collection while the toast is up; Undo follows it.
    const collectionNow = followCollection(addedTo);
    Swal.mixin({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      showCloseButton: true,
      timer: inserted ? 10000 : 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
        toast.querySelector("#undoAddFlashcard")?.addEventListener("click", () => {
          removeFlashcard(wordID, collectionNow());
          Swal.close();
        });
        addToastSwipeToDismiss();
      },
    }).fire(
      inserted
        ? {
            icon: "success",
            titleText: `Added "${shortNotifText}" to collection "${addedTo}"`,
            // A 48dp touch target around the small outlined button, which stays where it
            // was: negative margins take the extra padding back.
            html: `<button id="undoAddFlashcard" style="margin:-9px 0 -15px -12px;padding:15px 12px;background:none;border:0;cursor:pointer;font-family:inherit;font-size:12px;color:#555"><span style="display:block;border:1px solid #aaa;border-radius:4px;padding:2px 10px">Undo</span></button>`,
          }
        : { icon: "info", titleText: `"${shortNotifText}" is already in collection "${addedTo}"` }
    );

    if (inserted) {
      trackFlashcardAdd({ root, collectionName: addedTo, isNewCollection, collectionSize: (flashcards?.[addedTo] || []).length });
    }
    // button animation
    setFlashcardButtonPressed(true);
    setTimeout(() => {
      setFlashcardButtonPressed(false);
    }, 1000);
  };

  const handleAddToFlashcards = (collection) => {
    // if (addedToFlashcards) {
    //   setAddedToFlashcards(false);
    //   removeFlashcard(word, form);
    //   toastSuccess(`Successfully Removed ${shortNotifText} From Flashcards`);
    // } else {
    //   setAddedToFlashcards(true);

    const { collection: addedTo, inserted } = collection
      ? addFlashcard(word, form, flashcardDefinition(), root, wordID, collection)
      : addFlashcard(word, form, flashcardDefinition(), root, wordID);
    announceFlashcardAdd(addedTo, inserted, false);
  };

  const flashCardButtonStyles = {
    transition: "opacity 0.5s ease",
    opacity: flashcardButtonPressed ? 0.1 : 1,
  };

  const handleAddNewCollection = () => {
    Swal.fire({
      title: "Add New Collection",
      input: "text",
      inputPlaceholder: "Enter collection name",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) popup.style.fontFamily = "Mulish, sans-serif";
      },
      inputValidator: (value) => {
        if (!value) return "Collection name cannot be empty";
        if (isReservedCollectionName(value)) return "That name is reserved, please choose another";
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const newCollectionName = result.value;
        const isNewCollection = !Object.prototype.hasOwnProperty.call(flashcards || {}, newCollectionName);
        // Add once and announce once. (This used to add here AND again through
        // handleAddToFlashcards, whose duplicate-add Undo then deleted the card.)
        const inserted = addCollectionWithFlashcard(word, form, flashcardDefinition(), root, wordID, newCollectionName);
        announceFlashcardAdd(newCollectionName, inserted, isNewCollection);
      }
    });
  };

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        right: 0,
        p: 0,
        zIndex: 1,
        display: "flex",
      }}
    >
      {/* button to add to flashcards collection */}

      <Tooltip title={displayShort ? "Show Long Def" : "Show Short Def"}>
        <IconButton
          id="wt-toggle"
          size="small"
          onClick={(event) => {
            setDisplayShort(!displayShort);
          }}
          style={flashCardButtonStyles}
          // disabled={flashcardButtonPressed}
          sx={{
            ...cardButtonSx,
            color: displayShort ? "green" : "gray",
          }}
        >
          {displayShort ? (
            <AddCircleOutlineOutlinedIcon />
          ) : (
            <RemoveCircleOutlineOutlinedIcon />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Quick Add">
        <IconButton
          id="wt-flashcard"
          size="small"
          onClick={() => {
            if (getCollectionNames().length) {
              handleAddToFlashcards();
            } else {
              handleAddNewCollection();
            }
          }}
          style={flashCardButtonStyles}
          disabled={flashcardButtonPressed}
          sx={{
            ...cardButtonSx,
            color: addedToFlashcards ? "green" : "gray",
          }}
        >
          <LibraryAddOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add to Collection">
        <IconButton
          size="small"
          onClick={() => openCollectionSheet({ addTo: handleAddToFlashcards, addToNew: handleAddNewCollection })}
          style={flashCardButtonStyles}
          disabled={flashcardButtonPressed}
          sx={{
            ...cardButtonSx,
            color: addedToFlashcards ? "green" : "gray",
          }}
        >
          <StyleOutlinedIcon />
        </IconButton>
      </Tooltip>

      <CopyToClipboard text={`${textToCopy}`} onCopy={handleCopy}>
        <Tooltip title="Copy to Clipboard">
          <IconButton
            id="wt-copy"
            size="small"
            style={buttonStyles}
            disabled={copyButtonPressed}
            sx={{
              ...cardButtonSx,
              zIndex: 1,
              color: "gray",
            }}
          >
            <CopyIcon />
          </IconButton>
        </Tooltip>
      </CopyToClipboard>
    </Box>
  );
};

const Definition = () => {
  const [searchInput, setSearchInput] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [defIsClosing, setDefIsClosing] = useState(false);
  const defPaperRef = useRef(null);
  const defCloseTimerRef = useRef(null);
  const defLastSuggestionsRef = useRef([]);
  if (suggestions.length > 0) defLastSuggestionsRef.current = suggestions;
  const defSnapshotHeightRef = useRef(0);
  defSnapshotHeightRef.current = defPaperRef.current?.offsetHeight ?? defSnapshotHeightRef.current;
  const suggestTimerRef = useRef(null);
  const suggestSeqRef = useRef(0);

  // Closes the dropdown and drops any pending or in-flight autocomplete lookup, so
  // a late result can't reopen it.
  const clearSuggestions = () => {
    clearTimeout(suggestTimerRef.current);
    suggestSeqRef.current += 1;
    setSuggestions([]);
    setHighlightedIndex(-1);
  };
  useEffect(() => () => clearTimeout(suggestTimerRef.current), []);

  // Android's Back puts the keyboard away but leaves the field focused, so no blur closes
  // the list over the root banner. It closes with the keyboard; a tap on the field or more
  // typing opens it again.
  useEffect(() => onSoftKeyboardHidden((field) => {
    if (field === searchInputRef.current) clearSuggestions();
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // "/" focuses the search bar; "Escape" clears it.
  useSearchHotkeys(searchInputRef, () => {
    setSearchInput("");
    clearSuggestions();
  });

  // Press "n" to jump to the nouns section (cycles through them on multi-sense roots).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "n" && e.key !== "N") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Don't hijack typing in a field, or fire while a modal/dialog is open.
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (el && el.isContentEditable)) return;
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"]')) return;

      const sections = Array.from(document.querySelectorAll("[data-nouns-section]"));
      if (!sections.length) return;
      e.preventDefault();
      // Next nouns section below the current scroll position, wrapping to the first.
      const next = sections.find((s) => s.getBoundingClientRect().top > 100) || sections[0];
      next.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useLayoutEffect(() => {
    clearTimeout(defCloseTimerRef.current);

    if (suggestions.length === 0) {
      if (defSnapshotHeightRef.current > 0) {
        setDefIsClosing(true);
        defCloseTimerRef.current = setTimeout(() => {
          setDefIsClosing(false);
          defSnapshotHeightRef.current = 0;
        }, 160);
      }
      return;
    }

    setDefIsClosing(false);

    const paper = defPaperRef.current;
    if (!paper) return;
    const oldH = defSnapshotHeightRef.current;
    const newH = paper.offsetHeight;
    if (newH >= oldH || oldH === 0) return;
    paper.style.height = `${oldH}px`;
    paper.style.overflow = "hidden";
    paper.style.transition = "none";
    void paper.offsetHeight;
    paper.style.transition = "height 0.22s cubic-bezier(0.4,0,0.2,1)";
    paper.style.height = `${newH}px`;
    const t = setTimeout(() => {
      paper.style.height = "";
      paper.style.overflow = "";
      paper.style.transition = "";
    }, 240);
    return () => {
      clearTimeout(t);
      paper.style.height = "";
      paper.style.overflow = "";
      paper.style.transition = "";
    };
  }, [suggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    bookmarks,
    addBookmark,
    removeBookmark,
    addFlashcard,
    removeFlashcard,
    addCollection,
    flashcards,
    addToHistory,
    settings,
  } = useAppContext();

  // Opens a search key. `typed` is the spelling as entered, passed along like Home
  // does: the key has its hamza seats collapsed, which root analysis can't undo. The
  // same key typed another way (سوال, then سؤال) re-runs the search in place, if that
  // gives root analysis a different spelling to start from.
  const searchFor = (key, typed) => {
    const target = { pathname: searchPath(key), state: typed ? { rawQuery: typed } : undefined };
    if (key !== word) history.push(target);
    else if (!exist && typed && rootAnalysisSpelling(typed, key) !== rootAnalysisSpelling(rawQuery, key)) {
      history.replace(target);
    }
  };

  // handle submission of the search bar
  const handleInputSubmit = (event) => {
    event.preventDefault();
    const trimmedWord = searchInput.trim();
    const key = toSearchKey(trimmedWord);
    // Either toast closes the soft keyboard first, or on iOS it would be drawn under it.
    if (!key) {
      closeSoftKeyboard();
      toastError("Root to search cannot be empty");
      return;
    }
    // Letters the typing guide has no Arabic for ("book"): say so, as Home does,
    // rather than open a "Root not found" page for a key that can't match.
    const hint = transliterationHint(key);
    if (hint) {
      closeSoftKeyboard();
      toastTip(hint);
      return;
    }
    clearSuggestions();
    setSearchInput("");
    searchFor(key, trimmedWord);
  };

  // Only the newest lookup may fill the dropdown: the one-letter prefix scan is the
  // slowest and used to land after later ones.
  const lookUpSuggestions = (input, delay) => {
    clearTimeout(suggestTimerRef.current);
    const seq = ++suggestSeqRef.current;
    const key = toSearchKey(input);
    if (!key) { setSuggestions([]); return; }
    suggestTimerRef.current = setTimeout(async () => {
      try {
        const keys = (await getMatchingRoots(key)).slice(0, 10);
        // Listed as the dictionary spells them (قرأ, not the key قرا), searched by key.
        const spelled = await spellRootKeys(keys).catch(() => new Map());
        if (seq === suggestSeqRef.current) setSuggestions(keys.map((k) => ({ key: k, label: spelled.get(k) || k })));
      } catch (err) {
        console.warn("[Definition] autocomplete lookup failed:", err);
      }
    }, delay);
  };

  const handleSearchChange = (e) => {
    const input = e.target.value;
    setSearchInput(input);
    setHighlightedIndex(-1);
    lookUpSuggestions(input, 150); // debounced like Home
  };

  // The dropdown is open only while the field has focus; left open, it covered the
  // root banner. It comes back when the field is focused again.
  const handleSearchFocus = () => {
    if (searchInput.trim()) lookUpSuggestions(searchInput, 0);
  };

  // A tap on the field that kept focus when Back put the keyboard away brings no focus event.
  const handleSearchClick = () => {
    if (!suggestions.length && searchInput.trim()) lookUpSuggestions(searchInput, 0);
  };

  const handleSelectSuggestion = (selected) => {
    clearSuggestions();
    setSearchInput("");
    // The press kept the field focused (see the dropdown): close the soft keyboard as the
    // tap used to, for when the page stays (its own root picked).
    closeSoftKeyboard();
    searchFor(selected);
  };

  const handleSearchKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((p) => (p + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((p) => (p - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[highlightedIndex].key);
    }
  };

  const word = decodeRouteParam(useParams().word);
  const history = useHistory();
  const location = useLocation();
  // Back/forward, a reload or a restored page (a POP) brings no router state. A link
  // followed in the app (a History card, a suggestion) has no typed spelling to bring.
  const rawQuery = useMemo(
    () => location.state?.rawQuery ?? (history.action === "POP" ? rememberedRawQuery(word) : undefined),
    [location, history.action, word]
  );
  // const [definitions, setDefinitions] = useState([]);
  // const [nouns, setNouns] = useState([]);
  const [rootInfo, setRootInfo] = useState([]);
  const [exist, setExist] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [successfullyConnected, setSuccessfullyConnected] = useState(false);
  const [error, setError] = useState(null);
  const [sarfSuggestions, setSarfSuggestions] = useState([]);
  const [rootIsGuess, setRootIsGuess] = useState(false);
  const [missingRootFeedback, setMissingRootFeedback] = useState(null);
  const [collectionSheet, setCollectionSheet] = useState({ open: false, actions: null });
  const openCollectionSheet = useCallback((actions) => setCollectionSheet({ open: true, actions }), []);
  const closeCollectionSheet = () => setCollectionSheet((sheet) => ({ ...sheet, open: false }));

  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const walkthroughStarted = useRef(false);

  // The tour points at a root page's controls, so it only starts (and only counts as
  // seen) on a found root, not on a "Root not found" or derived-word page.
  useEffect(() => {
    if (loaded && exist && !walkthroughStarted.current && !localStorage.getItem("hasSeenDefinitionWalkthrough")) {
      const t = setTimeout(() => {
        walkthroughStarted.current = true;
        window.scrollTo({ top: 0, behavior: "instant" });
        localStorage.setItem("hasSeenDefinitionWalkthrough", "1");
        setShowWalkthrough(true);
      }, 700);
      return () => clearTimeout(t);
    }
  }, [loaded, exist]);

  // One-time desktop hint about the "n" jump-to-nouns shortcut. Only on a loaded
  // root that has nouns, only after the first-run walkthrough has been seen (and
  // not in the same session it ran), and never under an open dialog/tour overlay.
  // Gated on a desktop pointer, not width: a phone in landscape is wide but has no keyboard.
  useEffect(() => {
    const hasDesktopPointer = window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
    if (!hasDesktopPointer || !loaded || !exist || showWalkthrough || walkthroughStarted.current) return;
    if (!rootInfo.some((d) => d.nouns && d.nouns.length)) return;
    try {
      if (!localStorage.getItem("hasSeenDefinitionWalkthrough")) return;
      if (localStorage.getItem("hw-hint-definition")) return;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden), [role="dialog"], .shepherd-element')) return;
      toastTip("Press n to jump to the nouns.");
      try {
        localStorage.setItem("hw-hint-definition", "1");
      } catch {
        /* storage unavailable */
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [loaded, exist, showWalkthrough, rootInfo]);

  const handleWalkthroughDone = () => {
    localStorage.setItem("hasSeenDefinitionWalkthrough", "1");
    setShowWalkthrough(false);
  };

  // const [audio, setAudio] = useState(null);

  // A root is one bookmark however the page was opened (سأل, سال, سءل; رمى, which opens
  // رمي): stored keys are matched to the page's root by the rule the Bookmarks and History
  // pages group them by. One saved without its root counts by the key it was opened with.
  const routeKey = rootKey(word);
  const pageRootKey = rootIdentity(word, rootInfo[0]?.root);
  const bookmarkedKeys = useMemo(
    () =>
      Object.keys(bookmarks).filter(
        (stored) => rootKey(stored) === routeKey || rootIdentity(stored, bookmarks[stored]?.[0]?.root) === pageRootKey
      ),
    [bookmarks, routeKey, pageRootKey]
  );
  const isBookmarked = bookmarkedKeys.length > 0;

  const DottedDivider = styled(Divider)({
    border: 0,
    borderTop: "1px dotted #ccc",
    margin: "24px 0",
  });

  // updates state after retrieving root data from API
  function updateState(data) {
    const newRootInfo = [];
    data.forEach((element) => {
      newRootInfo.push({
        // The entry's own spelling (سأل) for display; `word` stays the search key.
        root: element["root"],
        definitions: element["definitions"],
        nouns: element["nouns"],
      });
    });

    setRootInfo(newRootInfo);
  }

  // Back/forward changes the word without going through searchFor: hide the previous
  // result (and a sheet opened from it) before the browser paints it under the new word.
  useLayoutEffect(() => {
    setLoaded(false);
    setCollectionSheet((sheet) => (sheet.open ? { ...sheet, open: false } : sheet));
  }, [word, rawQuery]);

  // handles retrieving data from API and sets state accordingly
  useEffect(() => {
    // A lookup for an earlier word can finish after this one; only the current
    // lookup may update the page (or record history and analytics).
    let active = true;
    // Always, even when it is the key itself or nothing was typed: an older spelling saved
    // on this entry, or for this key, would otherwise come back on Back/forward.
    rememberRawQuery(word, rawQuery || null);

    const fetchDefinition = async () => {
      console.log("Useeffect Fired");
      try {
        // retrieveAllWordsWithRoot normalizes + tokenizes the root itself, so pass
        // the raw param (handles hamza seats and slash/space compound roots).
        const data = await retrieveAllWordsWithRoot(word);
        if (!active) return;

        if (data.length > 0) {
          // ✅ Root exists, update state as usual
          updateState(data);
          setExist(true);
          setSuccessfullyConnected(true);
          setLoaded(true);
          setSarfSuggestions([]);
          // Under the route's own key, as released builds stored it, so this visit replaces
          // an entry saved before entries kept their root. With the root stored, History
          // shows the root's keys (رمى, رمي) as one card.
          addToHistory(word, data[0]);
          trackDefinitionViewed({
            root: word,
            rawQuery,
            entryCount: data.length,
            nounCount: data.reduce((sum, d) => sum + (d.nouns?.length || 0), 0),
          });
          return;
        }

        // ❌ No data from DB — detect the query's root(s) and suggest any that
        // exist. Pass rawQuery (original spelling) so the analyzer sees the real
        // hamza carriers, not the hamza-collapsed URL form.
        const { keys, ambiguous } = await getRootSuggestionsDetailed(word, rawQuery);
        // Each suggestion in the dictionary's spelling, for the ITS ROOT tiles.
        const entriesByKey = await getEntriesForRootKeys(keys).catch(() => new Map());
        if (!active) return;
        const suggestions = keys.map((key) => ({
          key,
          root: pickRootSpelling((entriesByKey.get(key) || []).map((doc) => entrySpelling(key, doc)), key, rawQuery),
        }));

        // An empty list also clears the previous query's suggestions.
        trackRootNotFound({ root: word, rawQuery, hadSarfSuggestions: suggestions.length > 0 });
        setSarfSuggestions(suggestions);
        // A homograph (هام: هامَ "to rove", هامّ "important") leaves the first root a
        // guess among equals, so it is offered as a possible root, not as "its root".
        setRootIsGuess(ambiguous);
        setExist(false);
        setSuccessfullyConnected(true);
        setLoaded(true);
      } catch (err) {
        if (!active) return;
        console.error(err);
        setError(err);
        setSarfSuggestions([]);
        setExist(false);
        setLoaded(true);
        setSuccessfullyConnected(false);

        Swal.fire({
          icon: "error",
          title: "Couldn't load this entry",
          // JSON.stringify(DOMException) is "{}", so show the message itself.
          text: err?.message || String(err),
          confirmButtonText: "Go back",
        }).then((result) => {
          if (result.isConfirmed) {
            goBackOrHome(history);
          }
        });
      }
    };

    // Always from the local dictionary, bookmarked or not: a bookmark's saved copy
    // can be out of date.
    fetchDefinition();
    return () => {
      active = false;
    };
  }, [word, rawQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the user typed, when it was Arabic: the key has its hamza seats collapsed
  // (سوال for سؤال). Latin input shows the key.
  const queryLabel = rawQuery && /[\u0600-\u06FF]/.test(rawQuery) ? rawQuery.trim() : word;
  // A found root is titled and labelled in the dictionary's own spelling (سأل, not the
  // key سال); homographs (راي, رأي) keep apart. Each label comes from the route's key, not
  // the route, which may have a hamza (رأي, سءل) the entry doesn't. A route spelled
  // otherwise than its key (a typed URL, an older bookmark) titles the page as typed would.
  const spellings = useMemo(() => rootInfo.map((d) => entrySpelling(routeKey, d)), [rootInfo, routeKey]);
  const pageTitle = pickRootSpelling(spellings, routeKey, rawQuery ?? (word !== routeKey ? word : undefined));

  // The search bar and its autocomplete, on both the root page and the
  // not-found / derived-word page.
  const renderSearchBar = (endAction) => (
    <Box sx={{ position: "relative", mb: 2 }}>
      <form onSubmit={handleInputSubmit}>
        <Box sx={{
          bgcolor: "white",
          borderRadius: 3,
          p: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0px 10px 25px rgba(0,0,0,0.05)",
          transition: "transform 0.2s",
          "&:focus-within": { transform: "scale(1.02)" },
        }}>
          <SearchIcon sx={{ color: "action.disabled" }} />
          <InputBase
            inputRef={searchInputRef}
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={handleSearchFocus}
            onBlur={clearSuggestions}
            placeholder="Search for a root"
            inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false, onClick: handleSearchClick }}
            sx={{ flex: 1, fontFamily: "inherit" }}
          />
          <IconButton
            type="submit"
            aria-label="Search"
            sx={{
              ...barButtonSx,
              color: "action.disabled",
              "&:hover": { bgcolor: "transparent" },
              "&:hover svg": { color: "black", bgcolor: "#BABABA", borderRadius: "50%" },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
          {endAction}
        </Box>
      </form>

      {(suggestions.length > 0 || defIsClosing) && (
        <Paper
          ref={defPaperRef}
          elevation={0}
          // Keep focus in the field while a suggestion is pressed, so the blur that
          // closes the dropdown doesn't remove it before the click selects.
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            mt: 0.75,
            borderRadius: "14px",
            border: "1px solid rgba(21,53,189,0.1)",
            boxShadow: "0px 8px 32px rgba(21,53,189,0.14)",
            overflow: "hidden",
            animation: defIsClosing
              ? "suggestionDropdownOut 0.15s ease-in forwards"
              : "suggestionDropdown 0.18s ease-out both",
          }}
        >
          <Box sx={{ maxHeight: 240, overflowY: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {(defIsClosing ? defLastSuggestionsRef.current : suggestions).map((s, i) => {
              const isArabic = /[\u0600-\u06FF]/.test(s.label);
              const isSelected = i === highlightedIndex;
              return (
                <Box
                  key={s.key}
                  onClick={() => handleSelectSuggestion(s.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    borderLeft: isSelected ? "3px solid #1535BD" : "3px solid transparent",
                    bgcolor: isSelected ? "rgba(21,53,189,0.06)" : "transparent",
                    transition: "background-color 0.12s, border-left 0.12s",
                    animation: "suggestionItem 0.15s ease-out both",
                    animationDelay: `${Math.min(i, 7) * 28}ms`,
                    "&:hover": { bgcolor: "rgba(21,53,189,0.06)", borderLeft: "3px solid #1535BD" },
                  }}
                >
                  <Typography sx={{
                    flex: 1,
                    fontSize: isArabic ? "1.1rem" : "0.95rem",
                    fontFamily: isArabic ? "'Noto Naskh Arabic', 'Scheherazade', serif" : "Mulish, sans-serif",
                    fontWeight: isArabic ? 500 : 600,
                    color: isSelected ? "#1535BD" : "#222",
                    lineHeight: 1.5,
                  }}>
                    {s.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );

  if (!loaded)
    // show loading sign
    return (
      <AlignCenterBox>
        <CircularProgress />
      </AlignCenterBox>
    );

  if (!successfullyConnected) {
    // The lookup failed; the catch block above already showed the error dialog.
    return (
      <Box sx={{ animation: "pageIn 0.22s ease-out both", pb: { xs: 14, sm: 6 } }}>
        <PageHeader title={queryLabel.toUpperCase()} />
        <Typography sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}>
          Couldn't load this entry{error?.message ? `: ${error.message}` : "."}
        </Typography>
      </Box>
    );
  }
  if (!exist) {
    return (
      <>
        <Box sx={{ animation: "pageIn 0.22s ease-out both", pb: { xs: 14, sm: 6 } }}>
          <PageHeader title={queryLabel.toUpperCase()} />
          {renderSearchBar()}

          <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
            <Paper elevation={0} sx={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              p: 4, borderRadius: 3, maxWidth: 380, width: "100%",
              border: "1px solid rgba(21,53,189,0.1)",
              boxShadow: "0px 10px 40px rgba(0,0,0,0.08)",
              animation: "milestoneIn 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.05s both",
            }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: "50%",
                bgcolor: "rgba(21,53,189,0.09)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <SearchOffOutlinedIcon sx={{ fontSize: 38, color: "#1535BD" }} />
              </Box>
              <Box sx={{ textAlign: "center", animation: "pageIn 0.22s ease-out 0.18s both" }}>
                <Typography sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1.25rem", mb: 0.75 }}>
                  {sarfSuggestions.length > 0 ? "That looks like a word" : "Root not found"}
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, overflowWrap: "anywhere" }}>
                  {sarfSuggestions.length > 0 ? (
                    <>The dictionary is organised by <strong>root</strong>, not by word — let's find the root of <strong>{queryLabel}</strong>.</>
                  ) : (
                    <>No results found for <strong>{queryLabel}</strong></>
                  )}
                </Typography>
              </Box>

              {sarfSuggestions.length > 0 && (
                <Box sx={{ width: "100%", mt: 0.5, animation: "pageIn 0.22s ease-out 0.26s both" }}>
                  {/* Teach how the searched word is built from its root, instead of
                      just naming the root. Falls back to a plain chip if the letters
                      can't be aligned (RootReveal handles that internally). */}
                  {/* The word's letters as root analysis read them (no harakat, and no
                      pasted «» or bidi marks drawn as tiles). */}
                  <RootReveal
                    word={rootAnalysisSpelling(rawQuery, word)}
                    root={sarfSuggestions[0].root}
                    tentative={rootIsGuess}
                    onExplore={() => searchFor(sarfSuggestions[0].key)}
                  />
                  {sarfSuggestions.length > 1 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mt: 2 }}>
                      <Typography sx={{ width: "100%", textAlign: "center", color: "#999", fontSize: "0.78rem" }}>
                        Other possible roots
                      </Typography>
                      {sarfSuggestions.slice(1).map((s) => (
                        <Chip
                          key={s.key}
                          label={s.root}
                          onClick={() => searchFor(s.key)}
                          sx={{ cursor: "pointer", borderColor: "#1535BD", color: "#1535BD", fontWeight: 600, direction: "rtl" }}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 0, width: "100%", mt: 1,
                animation: "pageIn 0.22s ease-out 0.3s both",
              }}>
                {/* When there's no root to teach, "Go back" is the main action, so
                    give it a touch more presence; otherwise keep it quiet under the
                    root card. */}
                <Button
                  variant={sarfSuggestions.length > 0 ? "text" : "contained"}
                  onClick={() => goBackOrHome(history)}
                  fullWidth={sarfSuggestions.length === 0}
                  sx={
                    sarfSuggestions.length > 0
                      ? { color: "#5b6270", fontFamily: "inherit", fontWeight: 600, fontSize: "0.9rem", borderRadius: "16px", textTransform: "none", px: 2, "&:hover": { color: "#1535BD", bgcolor: "rgba(21,53,189,0.05)" } }
                      : { bgcolor: "#1535BD", fontFamily: "inherit", fontWeight: 700, py: 1.25, fontSize: "1rem", borderRadius: "20px", textTransform: "none", "&:hover": { bgcolor: "#0F2EA8" } }
                  }
                >
                  Go back
                </Button>
                {sarfSuggestions.length === 0 && (
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => { trackReportMissingRoot({ root: word }); setMissingRootFeedback(queryLabel); }}
                    sx={{ mt: 1, color: "#888", fontFamily: "inherit", fontWeight: 500, fontSize: "0.85rem", borderRadius: "20px", textTransform: "none", "&:hover": { color: "#555", bgcolor: "transparent" } }}
                  >
                    Report missing root
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>

          <FeedbackDialog
            open={!!missingRootFeedback}
            onClose={() => setMissingRootFeedback(null)}
            mode="missing-root"
            prefillRoot={missingRootFeedback}
          />
        </Box>
      </>
    );
  }

  const isSearchMode = isMobile && !showScrollTop;

  return (
    <>
    <Box sx={{ animation: "pageIn 0.22s ease-out both", pb: { xs: 14, sm: 6 } }}>
      <PageHeader title={pageTitle} />
      {renderSearchBar(
        <IconButton
          id="wt-bookmark"
          aria-label={isBookmarked ? "Remove bookmark" : "Bookmark this root"}
          onClick={() => {
            if (isBookmarked) { bookmarkedKeys.forEach((key) => removeBookmark(key)); }
            // Saved under the root's key (سال, whether the page was opened as سأل or سءل;
            // رمي for رمى), so every spelling of a root makes one bookmark.
            else { addBookmark(pageRootKey, rootInfo); trackBookmarkAdd({ root: word, totalBookmarks: Object.keys(bookmarks).length + 1 }); }
          }}
          sx={{ ...barButtonSx, color: isBookmarked ? "#1535BD" : "action.disabled" }}
        >
          {isBookmarked ? <BookmarkedIcon fontSize="small" /> : <BookmarkIcon fontSize="small" />}
        </IconButton>
      )}

      <CollectionSheetContext.Provider value={openCollectionSheet}>
        <Box sx={{ zoom: settings?.fontScale ?? 1 }}>
          {rootInfo.length === 1 ? (
            <SingleDefinition word={word} label={spellings[0]} definition={rootInfo[0]} countString={null} />
          ) : (
            rootInfo.map((rootDefinition, index) => (
              <Fragment key={index}>
                <SingleDefinition
                  word={word}
                  label={spellings[index]}
                  definition={rootDefinition}
                  countString={`${index + 1} of ${rootInfo.length}`}
                />
                {index !== rootInfo.length - 1 && (
                  <Divider light={false} sx={{ display: "block", my: 3 }} />
                )}
              </Fragment>
            ))
          )}
        </Box>
      </CollectionSheetContext.Provider>

      <CollectionSheet open={collectionSheet.open} actions={collectionSheet.actions} onClose={closeCollectionSheet} />

      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Search for a root</DialogTitle>
        <DialogContent>
          <form
            onSubmit={(e) => {
              setSearchDialogOpen(false);
              handleInputSubmit(e);
            }}
          >
            <FilledInput
              autoFocus
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disableUnderline
              placeholder="Search for a root"
              inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
              sx={{
                mt: 1,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                "& .MuiFilledInput-input": { p: 2 },
              }}
              startAdornment={<SearchIcon color="disabled" />}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={(e) => {
              setSearchDialogOpen(false);
              handleInputSubmit(e);
            }}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackDialog
        open={!!missingRootFeedback}
        onClose={() => setMissingRootFeedback(null)}
        mode="missing-root"
        prefillRoot={missingRootFeedback}
      />

      {showWalkthrough && <Walkthrough onDone={handleWalkthroughDone} />}
    </Box>

    {(isMobile || showScrollTop) && (
      <Tooltip title={isSearchMode ? "Search" : "Scroll to top"}>
        <Box
          onClick={() => {
            if (isSearchMode) {
              searchInputRef.current?.focus();
              window.scrollTo({ top: 0, behavior: "instant" });
            } else {
              scrollToTop();
            }
          }}
          sx={{
            position: "fixed",
            bottom: { xs: "calc(env(safe-area-inset-bottom, 0px) + 80px)", sm: 24 },
            right: 16,
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "white",
            boxShadow: "0px 4px 16px rgba(21,53,189,0.15)",
            border: "1px solid rgba(21,53,189,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 10,
            animation: showScrollTop ? "none" : "scrollBtnIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both",
            transition: "background-color 0.15s, box-shadow 0.15s, transform 0.1s",
            // Only for a real pointer: Android's WebView leaves :hover on the last thing
            // tapped. Opaque, so the page doesn't show through the button.
            "@media (hover: hover)": {
              "&:hover": { backgroundColor: "#F3F5FC", boxShadow: "0px 6px 20px rgba(21,53,189,0.2)" },
            },
            "&:active": { transform: "scale(0.88)" },
          }}
        >
          <Box sx={{ position: "relative", width: 26, height: 26 }}>
            {/* Search icon — exits fast, enters after a short delay */}
            <Box sx={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              opacity: isSearchMode ? 1 : 0,
              transform: isSearchMode ? "scale(1) rotate(0deg)" : "scale(0.5) rotate(-90deg)",
              transition: isSearchMode
                ? "opacity 0.18s ease 0.1s, transform 0.22s ease 0.1s"
                : "opacity 0.1s ease, transform 0.12s ease",
            }}>
              <SearchIcon sx={{ fontSize: 22, color: "#1535BD" }} />
            </Box>
            {/* Arrow icon — exits fast, enters after a short delay */}
            <Box sx={{
              position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
              opacity: isSearchMode ? 0 : 1,
              transform: isSearchMode ? "scale(0.5) rotate(90deg)" : "scale(1) rotate(0deg)",
              transition: isSearchMode
                ? "opacity 0.1s ease, transform 0.12s ease"
                : "opacity 0.18s ease 0.1s, transform 0.22s ease 0.1s",
            }}>
              <KeyboardArrowUpIcon sx={{ fontSize: 26, color: "#1535BD" }} />
            </Box>
          </Box>
        </Box>
      </Tooltip>
    )}

    </>
  );
};

// renders a single form for the root definition
const DefinitionCard = ({ formEntry, i, countString, root }) => {
  const { settings } = useAppContext();
  const textColor = settings?.highContrastDefs ? "#000" : "#666";
  const [displayShort, setDisplayShort] = useState(false);

  return (
    <Box
      key={`FormBox-${i}-${countString}`}
      sx={{
        boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.05)",
        backgroundColor: "#fff",
        pt: 2,
        px: 2,
        pb: "44px",
        borderRadius: 2,
        mt: 3,
        position: "relative",
      }}
    >
      {/* render the box for holding buttons in bottom 
      right of form/noun card */}
      <CardButtons
        shortNotifText={`${formEntry.form} - ${formEntry.text}`}
        textToCopy={`${formEntry.form} - ${formEntry.text}\n${stripHTMLTags(
          formEntry.translation.text
        )}`}
        word={formEntry.text}
        form={formEntry.form}
        definition={
          displayShort
            ? formEntry.translation.short
            : formEntry.translation.text
        }
        transliteration={formEntry.transliteration || null}
        wordID={formEntry.id}
        root={root}
        displayShort={displayShort}
        setDisplayShort={setDisplayShort}
      ></CardButtons>

      <Typography
        sx={{ textTransform: "capitalize", color: textColor }}
        variant="subtitle1"
      >
        {`${formEntry.form} - ${formEntry.text}`}
      </Typography>
      <Typography
        sx={{ my: 0.5, color: textColor }}
        variant="body2"
        fontWeight={550}
        key={`FormTransliteration-${i}-${countString}`}
      >
        {formEntry.transliteration ? `${formEntry.transliteration}` : null}
      </Typography>
      <Typography
        sx={{ my: 1, color: textColor }}
        variant="body2"
        key={`FormEntry-${i}`}
        dangerouslySetInnerHTML={{
          __html: displayShort
            ? formEntry.translation.short
            : formEntry.translation.text,
        }}
      >
        {/* { {meaning.definitions.length > 1 && `${idx + 1}. `}{" "}} */}
      </Typography>
    </Box>
  );
};

// renders a single card for a noun
const NounCard = ({ nounEntry, i, countString, root }) => {
  const { settings } = useAppContext();
  const textColor = settings?.highContrastDefs ? "#000" : "#666";
  const [displayShort, setDisplayShort] = useState(false);

  let transliterations = nounEntry.transliteration
    ? `${nounEntry.transliteration}`
    : null;
  if (transliterations && nounEntry.plural.transliteration) {
    transliterations = `${transliterations} pl. ${nounEntry.plural.transliteration}`;
  }

  return (
    <Box
      key={`NounBox-${i}-${countString}`}
      sx={{
        boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.05)",
        backgroundColor: "#fff",
        pt: 2,
        px: 2,
        pb: "44px",
        borderRadius: 2,
        mt: 3,
        position: "relative",
      }}
    >
      <CardButtons
        shortNotifText={`${nounEntry["text"]} ${
          nounEntry["plural"]["text"]
            ? `pl. ${nounEntry["plural"]["text"]}`
            : ""
        }`}
        textToCopy={`${nounEntry["text"]} ${
          nounEntry["plural"]["text"]
            ? `pl. ${nounEntry["plural"]["text"]}`
            : ""
        } \n${stripHTMLTags(nounEntry["translation"]["text"])}`}
        word={nounEntry.text}
        definition={
          displayShort
            ? nounEntry.translation.short
            : nounEntry.translation.text
        }
        transliteration={transliterations || null}
        wordID={nounEntry.id}
        root={root}
        displayShort={displayShort}
        setDisplayShort={setDisplayShort}
      />

      <Typography sx={{ color: textColor }} variant="subtitle1">
        {`${nounEntry["text"]} ${
          nounEntry["plural"]["text"]
            ? `pl. ${nounEntry["plural"]["text"]}`
            : ""
        }`}
      </Typography>
      <Typography
        sx={{ my: 0.5, color: textColor }}
        variant="body2"
        fontWeight={550}
        key={`NounsEntry-${i}-${countString}`}
      >
        {transliterations}
        {/* {nounEntry.transliteration ? `${nounEntry.transliteration}` : null} */}
      </Typography>
      <Typography
        sx={{ my: 1, color: textColor }}
        variant="body2"
        key={`NounsDef-${i}-${countString}`}
        dangerouslySetInnerHTML={{
          __html: displayShort
            ? nounEntry["translation"]["short"]
            : nounEntry["translation"]["text"],
        }}
      ></Typography>
    </Box>
  );
};

// `word` is the search key (flashcards and links use it); `label` is the entry's
// own spelling of the root, shown in the banner.
const SingleDefinition = ({ word, label, definition, countString }) => {
  const { settings } = useAppContext();
  const [reportErrorOpen, setReportErrorOpen] = useState(false);

  const handleReportError = () => {
    setReportErrorOpen(true);
  };

  const handleCloseReportError = () => {
    setReportErrorOpen(false);
  };

  return (
    <>
      <Tooltip title="Root">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: 3,
            background: "linear-gradient(90.17deg, #191E5D 0.14%, #161F75 98.58%)",
            boxShadow: "0px 10px 20px rgba(19, 23, 71, 0.25)",
            px: 4,
            py: 5,
            color: "white",
            borderRadius: 2,
            position: "relative", // add this to make position absolute work
          }}
        >
          {countString && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "20px",
                px: 1.5,
                py: 0.3,
                fontSize: "11px",
                fontWeight: 600,
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.3px",
                userSelect: "none",
              }}
            >
              {countString}
            </Box>
          )}
          <Typography sx={{ textTransform: "capitalize" }} variant="h5">
            {label || word}
          </Typography>

          {/* Suggest a correction */}
          <Tooltip title="Suggest a correction">
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 48,
                height: 48,
                color: "rgba(255,255,255,0.45)",
                transition: "color 0.2s",
                "&:hover": { color: "rgba(255,255,255,0.9)" },
                "& svg": {
                  fontSize: 15,
                },
              }}
              onClick={handleReportError}
            >
              <RateReviewOutlinedIcon />
            </IconButton>
          </Tooltip>

        </Stack>
      </Tooltip>

      <FeedbackDialog
        open={reportErrorOpen}
        onClose={handleCloseReportError}
        mode="report-error"
        prefillRoot={label || word}
      />

      <Fragment key={1}>
        <Divider varianr="inset" light={true} sx={{ display: "none", my: 3 }} />

        {definition["definitions"]?.length ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 4,
              mb: 1.5,
              pb: 1,
              borderBottom: "2px solid rgba(21,53,189,0.18)",
              // A heading, like the app's chrome: a long press shouldn't select it. The
              // entries below stay selectable.
              userSelect: "none",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.95rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#1535BD",
              }}
            >
              Verbs
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Noto Naskh Arabic', 'Scheherazade', serif",
                fontSize: "1.5rem",
                lineHeight: 1,
                color: "#1535BD",
              }}
            >
              أفعال
            </Typography>
          </Box>
        ) : null}

        {definition["definitions"].map((formEntry, index) => (
          <DefinitionCard
            formEntry={formEntry}
            i={index}
            key={`DefinitionCard-${countString}-${index}`}
            countString={countString}
            root={word}
          />
        ))}
      </Fragment>

      {definition["nouns"].length ? (
        <Fragment key={`NounsBlock-${countString}`}>
          <Box
            data-nouns-section="true"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 4,
              mb: 1.5,
              pb: 1,
              borderBottom: "2px solid rgba(21,53,189,0.18)",
              // Land below the sticky PageHeader when jumped to (via the "n" shortcut).
              scrollMarginTop: "80px",
              userSelect: "none",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.95rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#1535BD",
              }}
              key={`NounsCard-${countString}`}
            >
              Nouns
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Noto Naskh Arabic', 'Scheherazade', serif",
                fontSize: "1.5rem",
                lineHeight: 1,
                color: "#1535BD",
              }}
            >
              أسماء
            </Typography>
          </Box>

          {definition["nouns"] &&
            definition["nouns"].map((nounEntry, i) => (
              <NounCard
                nounEntry={nounEntry}
                i={i}
                key={`NounCard-${countString}-${i}`}
                countString={countString}
                root={word}
              />
            ))}
        </Fragment>
      ) : null}
    </>
  );
};

export default Definition;
